// hooks/useSidebarCollections.ts  (REPLACE FILE)
import { useMemo } from "react";
import { useCollections } from "@/hooks/useCollections";

/**
 * Represents a collection node in the sidebar tree structure
 */
export interface SidebarCollection {
  id: string;
  name: string;
  parent_id: string | null;
  is_system: boolean;
  slug?: string | null;
  children: SidebarCollection[];
}

/**
 * Recursively maps collection nodes to SidebarCollection structure
 * @param nodes - Array of collection nodes from useCollections
 * @returns Array of typed SidebarCollection nodes with children
 */
function mapTree(nodes: any[]): SidebarCollection[] {
  // nodes is already a tree from useCollections.collectionsTree
  return nodes.map((n) => ({
    id: n.id,
    name: n.name,
    parent_id: n.parent_id,
    is_system: n.is_system,
    slug: n.slug ?? null,
    children: mapTree(n.children || []),
  }));
}

/**
 * Custom hook for managing sidebar collections
 *
 * Wraps useCollections to provide a simplified interface for sidebar navigation.
 * Returns collections organized in a hierarchical tree structure with loading state
 * and refetch capabilities.
 *
 * @param enabled - Whether to fetch collections data (default: true)
 * @returns Object containing collections array, loading state, and refetch function
 *
 * @example
 * ```tsx
 * // Usage in sidebar context
 * function SidebarProvider({ children }) {
 *   const { collections, loading, refetch } = useSidebarCollections(true);
 *
 *   return (
 *     <SidebarContext.Provider value={{ collections, loading, refetch }}>
 *       {children}
 *     </SidebarContext.Provider>
 *   );
 * }
 * ```
 */
export function useSidebarCollections(enabled: boolean = true) {
  const { collectionsTree, loading, refetch } = useCollections({ enabled, realtime: enabled });

  const collections = useMemo(() => mapTree(collectionsTree), [collectionsTree]);

  return { collections, loading, refetch };
}
